import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import {
  Timer as TimerIcon,
  Flag as FlagIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  PlayArrow as PlayIcon,
  Info as InfoIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { api } from '../../services/api';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';

interface ExamQuestion {
  id: number;
  questionText: string;
  imageName: string | null;
  category: string;
  options: Array<{
    id: number;
    optionText: string;
  }>;
}

interface ExamHistoryItem {
  id: number;
  score: number;
  passed: boolean;
  startedAt: string;
  completedAt: string;
}

interface ReviewItem {
  id: number;
  questionText: string;
  explanation: string;
  category: string;
  selectedOptionId: number | null;
  correctOptionId: number;
  isCorrect: boolean;
  options: Array<{
    id: number;
    optionText: string;
    isCorrect: boolean;
  }>;
}

export const MockTheoryTest: React.FC = () => {
  const confirm = useConfirm();
  const showToast = useToast();

  // Modes: 'START' | 'EXAM' | 'RESULT'
  const [mode, setMode] = useState<'START' | 'EXAM' | 'RESULT'>('START');
  
  // Loading & Errors
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<ExamHistoryItem[]>([]);

  // Active Exam state
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  
  // Answers mapping: questionId -> selectedOptionId
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  // Flagged questions mapping: questionId -> boolean
  const [flagged, setFlagged] = useState<{ [key: number]: boolean }>({});
  
  // Timer: remaining seconds
  const [timeLeft, setTimeLeft] = useState(57 * 60);

  // Results state
  const [result, setResult] = useState<{
    score: number;
    totalQuestions: number;
    passed: boolean;
    review: ReviewItem[];
  } | null>(null);
  
  // AI Study Buddy state
  const [analysis, setAnalysis] = useState<any>(null);

  // Fetch exam history and analysis on mount
  useEffect(() => {
    fetchHistoryAndAnalysis();
  }, []);

  // Timer countdown hook
  useEffect(() => {
    if (mode !== 'EXAM') return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [mode, questions, answers, sessionId]);

  const fetchHistoryAndAnalysis = async () => {
    try {
      setLoading(true);
      const res = await api.getTheoryExamHistory();
      setHistory(res.data || []);
      
      const analysisRes = await api.getTheoryAnalysis();
      setAnalysis(analysisRes.data);
    } catch (err) {
      console.error('Failed to load exam history/analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartExam = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.startTheoryExam();
      if (res.data) {
        setSessionId(res.data.sessionId);
        setQuestions(res.data.questions || []);
        setAnswers({});
        setFlagged({});
        setCurrentIdx(0);
        setTimeLeft(57 * 60);
        setMode('EXAM');
      }
    } catch (err: any) {
      console.error('Failed to start exam:', err);
      setError(err.response?.data?.message || 'Could not start exam session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartWeakestExam = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.startWeakestExam();
      if (res.data) {
        setSessionId(res.data.sessionId);
        setQuestions(res.data.questions || []);
        setAnswers({});
        setFlagged({});
        setCurrentIdx(0);
        setTimeLeft(30 * 60); // 30 minutes for 25 questions
        setMode('EXAM');
      }
    } catch (err: any) {
      console.error('Failed to start focused exam:', err);
      setError(err.response?.data?.message || 'Could not start focused exam session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (optionId: number) => {
    const qId = questions[currentIdx].id;
    setAnswers((prev) => ({
      ...prev,
      [qId]: optionId
    }));
  };

  const toggleFlag = () => {
    const qId = questions[currentIdx].id;
    setFlagged((prev) => ({
      ...prev,
      [qId]: !prev[qId]
    }));
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAutoSubmit = () => {
    submitExam(true);
  };

  const handleSubmitClick = async () => {
    const isConfirmed = await confirm({
      title: 'Submit Theory Test',
      message: 'Are you sure you want to submit your theory test?',
      confirmText: 'Yes, Submit',
      cancelText: 'Cancel',
      severity: 'info'
    });
    if (isConfirmed) {
      submitExam(false);
    }
  };

  const submitExam = async (isTimeOut = false) => {
    if (!sessionId) return;
    try {
      setSubmitting(true);
      setError(null);
      
      const payloadAnswers = Object.entries(answers).map(([qId, optId]) => ({
        questionId: parseInt(qId),
        selectedOptionId: optId
      }));

      const res = await api.submitTheoryExam(sessionId, payloadAnswers);
      if (res.data) {
        setResult(res.data);
        setMode('RESULT');
        fetchHistoryAndAnalysis(); // refresh history list
        if (isTimeOut) {
          showToast('Time has expired! Your exam was automatically submitted.', 'warning');
        }
      }
    } catch (err: any) {
      console.error('Submission failed:', err);
      setError(err.response?.data?.message || 'Failed to submit exam answers.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* HEADER */}
      <Box display="flex" alignItems="center" gap={1.5} mb={4}>
        <SchoolIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'extrabold', color: '#1a237e' }}>
          UK Driving Theory Mock Exam
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
      )}

      {/* START MODE */}
      {mode === 'START' && (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e' }}>
                  Prepare for your Theory Test
                </Typography>
                <Typography variant="body1" color="textSecondary" paragraph>
                  Practice using the OGL-compliant driving theory questions designed to mimic the DVSA exam format. Test your road knowledge, signs, stopping distances, and alertness in a timed environment.
                </Typography>
                
                <Box sx={{ mt: 3, mb: 4 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                    Exam Rules & Format:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}><TimerIcon color="primary" fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Time Limit: 57 minutes (DVSA standard)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}><SchoolIcon color="primary" fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Questions: 50 randomly selected mock questions" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Pass Mark: 85% score required (43 out of 50 correct answers)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}><FlagIcon color="warning" fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Flag for Review: Tag complex questions and solve them later" />
                    </ListItem>
                  </List>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleStartExam}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                  sx={{ py: 1.8, px: 4, fontWeight: 'bold', borderRadius: 2, width: '100%' }}
                >
                  {loading ? 'Initializing Exam...' : 'Start Mock Theory Test'}
                </Button>
              </CardContent>
            </Card>

            {analysis && analysis.hasData && (
              <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 4px 16px rgba(0,0,0,0.03)', mt: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon color="primary" /> AI Study Buddy
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Based on your previous {analysis.totalTests} mock tests, here is your category-based performance breakdown:
                  </Typography>
                  
                  <Box display="flex" flexDirection="column" gap={2} mb={3}>
                    {analysis.categoryStats.map((stat: any) => {
                      let color: "error" | "warning" | "success" = "success";
                      if (stat.Percentage < 50) color = "error";
                      else if (stat.Percentage < 85) color = "warning";
                      
                      return (
                        <Box key={stat.Category}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" fontWeight="medium">{stat.Category}</Typography>
                            <Typography variant="body2" fontWeight="bold" color={`${color}.main`}>
                              {stat.Percentage}% ({stat.CorrectQuestions}/{stat.TotalQuestions})
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={stat.Percentage} 
                            color={color}
                            sx={{ height: 6, borderRadius: 3 }} 
                          />
                        </Box>
                      );
                    })}
                  </Box>

                  {analysis.recommendations && analysis.recommendations.length > 0 && (
                    <Box sx={{ bgcolor: 'rgba(25, 118, 210, 0.04)', p: 2, borderRadius: 2, mb: 3, borderLeft: '4px solid #1976d2' }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                        Study Recommendations:
                      </Typography>
                      {analysis.recommendations.map((rec: string, idx: number) => (
                        <Typography key={idx} variant="body2" color="textPrimary" sx={{ mb: 0.5 }}>
                          • {rec}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {analysis.weakCategories && analysis.weakCategories.length > 0 && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleStartWeakestExam}
                      disabled={loading}
                      sx={{ py: 1.2, px: 3, fontWeight: 'bold', borderRadius: 2, width: '100%', textTransform: 'none' }}
                    >
                      Start Focused Test on Weak Categories (25 Qs)
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {analysis && !analysis.hasData && (
              <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 4px 16px rgba(0,0,0,0.03)', mt: 3, bgcolor: 'rgba(25, 118, 210, 0.01)' }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="#1a237e">
                    🧠 AI Study Buddy
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Complete your first mock theory test to unlock category-based study analysis and custom practice sets targeted to your weak areas!
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon /> Your Test History
                </Typography>

                {history.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <InfoIcon color="disabled" sx={{ fontSize: 48, mb: 1.5 }} />
                    <Typography variant="body2" color="textSecondary">
                      No attempts registered yet. Pass your first mock exam today!
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 380 }}>
                    <Table stickyHeader>
                      <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Score</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              {format(new Date(item.startedAt), 'dd.MM.yyyy - HH:mm')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              {item.score} / 50
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.passed ? 'PASS' : 'FAIL'}
                                size="small"
                                color={item.passed ? 'success' : 'error'}
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* EXAM ACTIVE MODE */}
      {mode === 'EXAM' && questions.length > 0 && (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
              <CardContent sx={{ p: 4 }}>
                {/* Timer and category bar */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Chip 
                    label={questions[currentIdx].category} 
                    color="secondary" 
                    variant="outlined" 
                    size="small" 
                    sx={{ fontWeight: 'bold' }} 
                  />
                  
                  <Box display="flex" alignItems="center" gap={1} sx={{ color: timeLeft < 300 ? 'error.main' : 'text.primary' }}>
                    <TimerIcon />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {formatTime(timeLeft)}
                    </Typography>
                  </Box>
                </Box>

                {/* Progress bar */}
                <Box sx={{ width: '100%', mb: 4 }}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="textSecondary">
                      Question {currentIdx + 1} of {questions.length}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {Object.keys(answers).length} Answered
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={((currentIdx + 1) / questions.length) * 100} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                {/* Question content */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                    {questions[currentIdx].questionText}
                  </Typography>

                  {questions[currentIdx].imageName && (
                    <Box sx={{ my: 2, textAlign: 'center' }}>
                      <img 
                        src={`/images/${questions[currentIdx].imageName}`} 
                        alt="Road sign / hazard visual aid" 
                        style={{ maxWidth: '100%', height: 'auto', maxHeight: 200, borderRadius: 8 }} 
                      />
                    </Box>
                  )}

                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={answers[questions[currentIdx].id] || ''}
                      onChange={(e) => handleSelectOption(parseInt(e.target.value))}
                    >
                      {questions[currentIdx].options.map((opt) => (
                        <Paper
                          key={opt.id}
                          variant="outlined"
                          sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                            borderColor: answers[questions[currentIdx].id] === opt.id ? 'primary.main' : '#e0e0e0',
                            bgcolor: answers[questions[currentIdx].id] === opt.id ? 'rgba(25, 118, 210, 0.02)' : 'inherit',
                            transition: 'border-color 0.2s',
                            '&:hover': { borderColor: 'primary.main' }
                          }}
                        >
                          <FormControlLabel
                            value={opt.id}
                            control={<Radio />}
                            label={<Typography variant="body1">{opt.optionText}</Typography>}
                            sx={{ width: '100%', m: 0 }}
                          />
                        </Paper>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* Footer Controls */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                  <Box display="flex" gap={1.5}>
                    <Button
                      variant="outlined"
                      disabled={currentIdx === 0}
                      onClick={() => setCurrentIdx((p) => p - 1)}
                      startIcon={<ArrowBackIcon />}
                      sx={{ fontWeight: 'bold', borderRadius: 2 }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={currentIdx === questions.length - 1}
                      onClick={() => setCurrentIdx((p) => p + 1)}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ fontWeight: 'bold', borderRadius: 2 }}
                    >
                      Next
                    </Button>
                  </Box>

                  <Button
                    variant={flagged[questions[currentIdx].id] ? 'contained' : 'outlined'}
                    color="warning"
                    onClick={toggleFlag}
                    startIcon={<FlagIcon />}
                    sx={{ fontWeight: 'bold', borderRadius: 2 }}
                  >
                    {flagged[questions[currentIdx].id] ? 'Flagged' : 'Flag for Later'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e' }}>
                  Question Navigation Map
                </Typography>
                
                <Grid container spacing={1} sx={{ mb: 4 }}>
                  {questions.map((q, idx) => {
                    const isAnswered = answers[q.id] !== undefined;
                    const isFlagged = flagged[q.id] === true;
                    const isCurrent = idx === currentIdx;

                    let btnColor = 'inherit';
                    let borderStyle = '1px solid #e0e0e0';
                    let bg = '#ffffff';

                    if (isAnswered) {
                      bg = 'rgba(25, 118, 210, 0.08)';
                      borderStyle = '1px solid #1976d2';
                    }
                    if (isFlagged) {
                      bg = 'rgba(237, 108, 2, 0.15)';
                      borderStyle = '1px solid #ed6c02';
                    }
                    if (isCurrent) {
                      borderStyle = '2.5px solid #1a237e';
                    }

                    return (
                      <Grid size={{ xs: 2.4 }} key={q.id}>
                        <Button
                          variant="outlined"
                          onClick={() => setCurrentIdx(idx)}
                          sx={{
                            width: '100%',
                            minWidth: 0,
                            p: 1.5,
                            fontWeight: 'bold',
                            border: borderStyle,
                            bgcolor: bg,
                            color: isCurrent ? '#1a237e' : 'text.primary',
                            '&:hover': {
                              border: borderStyle,
                              bgcolor: bg
                            }
                          }}
                        >
                          {idx + 1}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box display="flex" flexDirection="column" gap={1.5} sx={{ mt: 2 }}>
                  <Box display="flex" gap={1} alignItems="center">
                    <Box sx={{ width: 16, height: 16, bgcolor: 'rgba(25, 118, 210, 0.08)', border: '1px solid #1976d2', borderRadius: 0.5 }} />
                    <Typography variant="caption">Answered</Typography>
                  </Box>
                  <Box display="flex" gap={1} alignItems="center">
                    <Box sx={{ width: 16, height: 16, bgcolor: 'rgba(237, 108, 2, 0.15)', border: '1px solid #ed6c02', borderRadius: 0.5 }} />
                    <Typography variant="caption">Flagged for Review</Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={handleSubmitClick}
                  disabled={submitting}
                  sx={{ width: '100%', mt: 4, py: 1.8, fontWeight: 'bold', borderRadius: 2 }}
                >
                  {submitting ? 'Submitting Test...' : 'Finish & Submit Exam'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* RESULTS / REVIEW MODE */}
      {mode === 'RESULT' && result && (
        <Box display="flex" flexDirection="column" gap={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
            <Box sx={{ p: 4, bgcolor: result.passed ? 'success.dark' : 'error.dark', color: 'white', textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'medium', mb: 1, textTransform: 'uppercase' }}>
                Mock Exam Results
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 'black', mb: 2 }}>
                {result.score} / {result.totalQuestions}
              </Typography>
              <Chip
                label={result.passed ? 'PASSED (PASS MARK: 85%)' : 'FAILED (PASS MARK: 85%)'}
                color={result.passed ? 'success' : 'error'}
                sx={{
                  fontWeight: 'extrabold',
                  fontSize: '1rem',
                  py: 2.2,
                  px: 3,
                  bgcolor: '#ffffff',
                  color: result.passed ? 'success.dark' : 'error.dark'
                }}
              />
            </Box>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" paragraph>
                {result.passed
                  ? 'Congratulations! You achieved the score required to pass the official theory test. Keep practicing to retain your knowledge!'
                  : 'Unfortunately, your score was below the 85% requirement. Review the incorrect answers below to improve your driving theory knowledge.'}
              </Typography>
              <Button
                variant="contained"
                onClick={() => setMode('START')}
                sx={{ mt: 2, px: 6, py: 1.5, fontWeight: 'bold', borderRadius: 2 }}
              >
                Exit Exam Room
              </Button>
            </CardContent>
          </Card>

          {/* DETAILED QUESTIONS REVIEW */}
          <Typography variant="h5" sx={{ fontWeight: 'extrabold', color: '#1a237e', mt: 2 }}>
            Review Exam Questions & Highway Code Explanations
          </Typography>

          <Box display="flex" flexDirection="column" gap={3}>
            {result.review.map((item, idx) => (
              <Card 
                key={item.id} 
                sx={{ 
                  borderRadius: 3, 
                  border: '1px solid',
                  borderColor: item.isCorrect ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                  bgcolor: item.isCorrect ? 'rgba(76, 175, 80, 0.01)' : 'rgba(244, 67, 54, 0.01)'
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={2.5}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {idx + 1}. {item.questionText}
                    </Typography>
                    <Chip 
                      label={item.category} 
                      size="small" 
                      variant="outlined" 
                      color="secondary" 
                      sx={{ fontWeight: 'bold' }} 
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
                    {item.options.map((opt) => {
                      const isSelected = item.selectedOptionId === opt.id;
                      const isCorrect = opt.isCorrect;

                      let borderColor = '#e0e0e0';
                      let bg = 'inherit';
                      let label = '';
                      let icon = null;

                      if (isCorrect) {
                        borderColor = 'success.main';
                        bg = 'rgba(76, 175, 80, 0.06)';
                        label = 'Correct Answer';
                        icon = <CheckIcon color="success" />;
                      } else if (isSelected) {
                        borderColor = 'error.main';
                        bg = 'rgba(244, 67, 54, 0.06)';
                        label = 'Your Incorrect Answer';
                        icon = <CancelIcon color="error" />;
                      }

                      return (
                        <Paper
                          key={opt.id}
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            borderColor,
                            bgcolor: bg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2
                          }}
                        >
                          <Typography variant="body2">{opt.optionText}</Typography>
                          {label && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="caption" fontWeight="bold" color={isCorrect ? 'success.main' : 'error.main'}>
                                {label}
                              </Typography>
                              {icon}
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>

                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 2.5, 
                      bgcolor: 'rgba(25, 118, 210, 0.02)',
                      borderColor: 'rgba(25, 118, 210, 0.15)',
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start'
                    }}
                  >
                    <SchoolIcon color="primary" sx={{ mt: 0.2 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0.5 }}>
                        Highway Code Explanation
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.explanation}
                      </Typography>
                    </Box>
                  </Paper>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Container>
  );
};
